"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AbstractClient = exports.defaults = void 0;
var tslib_1 = require("tslib");
/*
Copyright (C) 2009 - 2020 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
var axios_1 = tslib_1.__importDefault(require("axios"));
var qs = tslib_1.__importStar(require("query-string"));
var types_1 = require("./types");
var utils_1 = require("./utils");
exports.defaults = {
    headers: {
        Accept: 'application/json'
    },
    paramsSerializer: function (params) {
        return qs.stringify(params, {
            arrayFormat: 'none',
            skipNull: true,
            skipEmptyString: true
        });
    }
};
var AbstractClient = /** @class */ (function () {
    function AbstractClient(options) {
        var _a = determineClientURL(options), baseURL = _a.baseURL, baseHost = _a.baseHost, basePath = _a.basePath;
        var agentDefaults = utils_1.mergeConfigs(tslib_1.__assign(tslib_1.__assign({}, exports.defaults), { baseURL: baseURL }), options === null || options === void 0 ? void 0 : options.defaults);
        this.agent = (options === null || options === void 0 ? void 0 : options.agent) || axios_1.default.create(agentDefaults);
        this.applicationToken = options === null || options === void 0 ? void 0 : options.applicationToken;
        this.locale = options === null || options === void 0 ? void 0 : options.locale;
        this.preview = options === null || options === void 0 ? void 0 : options.preview;
        this.baseHost = baseHost;
        this.basePath = basePath;
    }
    AbstractClient.prototype.getAgent = function () {
        return this.agent;
    };
    /**
     * Executes an HTTP request call.
     *
     * @param config the request config
     * @param [options] additional options to customize the call
     * @return the HTTP response
     */
    AbstractClient.prototype.call = function (config, options) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        attachAccessTokenIfPresent(config, options === null || options === void 0 ? void 0 : options.accessToken);
                        attachApplicationTokenIfPresent(config, (options === null || options === void 0 ? void 0 : options.applicationToken) || this.applicationToken);
                        attachAcceptLanguageHeaderIfPresent(config, (options === null || options === void 0 ? void 0 : options.locale) || this.locale);
                        attachPreviewOptionsIfPresent(config, (options === null || options === void 0 ? void 0 : options.preview) || this.preview);
                        attachCancelTokenIfPresent(config, options === null || options === void 0 ? void 0 : options.cancelToken);
                        config = utils_1.mergeConfigs(config, { headers: options === null || options === void 0 ? void 0 : options.headers }, { params: options === null || options === void 0 ? void 0 : options.params }, options === null || options === void 0 ? void 0 : options.config);
                        if (!(options === null || options === void 0 ? void 0 : options.configCustomizer)) return [3 /*break*/, 2];
                        return [4 /*yield*/, options.configCustomizer(config)];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2: return [4 /*yield*/, this.getAgent().request(config)];
                    case 3: return [2 /*return*/, _a.sent()];
                }
            });
        });
    };
    /**
     * Attaches an interceptor function to the axios request interceptor manager
     * and returns a function that will detach it.
     *
     * @param onFulfilled the function that executes before the request executes
     * @param onRejected the function that executes when the request fails to execute
     * @return a function to detach the interceptor
     */
    AbstractClient.prototype.requestInterceptor = function (onFulfilled, onRejected) {
        var _this = this;
        var interceptorHandle = this.getAgent().interceptors.request.use(onFulfilled, onRejected);
        return function () { return _this.getAgent().interceptors.request.eject(interceptorHandle); };
    };
    /**
     * Attaches an interceptor function to the axios response interceptor manager
     * and returns a function that will detach it.
     *
     * @param onFulfilled the function that executes after a successful response
     * @param onRejected the function that executes after a failure response
     * @return a function to detach the interceptor
     */
    AbstractClient.prototype.responseInterceptor = function (onFulfilled, onRejected) {
        var _this = this;
        var interceptorHandle = this.getAgent().interceptors.response.use(onFulfilled, onRejected);
        return function () { return _this.getAgent().interceptors.response.eject(interceptorHandle); };
    };
    return AbstractClient;
}());
exports.AbstractClient = AbstractClient;
function determineClientURL(options) {
    if (options === null || options === void 0 ? void 0 : options.baseURL) {
        var url = new URL(options.baseURL);
        return {
            baseURL: options.baseURL,
            baseHost: "" + url.origin + url.host,
            basePath: url.pathname
        };
    }
    else {
        return {
            baseURL: createBaseURL(options.baseHost, options.basePath),
            baseHost: options.baseHost,
            basePath: options.basePath
        };
    }
}
function createBaseURL(baseHost, basePath) {
    if (baseHost) {
        if (basePath) {
            return "" + baseHost + basePath;
        }
        return baseHost;
    }
    return basePath;
}
function attachAccessTokenIfPresent(config, accessToken) {
    if (accessToken) {
        if (!config.headers) {
            config.headers = {};
        }
        if (accessToken.startsWith('Bearer ')) {
            config.headers[types_1.DefaultRequestHeaderType.AUTHORIZATION] = accessToken;
        }
        else {
            config.headers[types_1.DefaultRequestHeaderType.AUTHORIZATION] = "Bearer " + accessToken;
        }
    }
}
function attachApplicationTokenIfPresent(config, applicationToken) {
    if (applicationToken) {
        if (!config.headers) {
            config.headers = {};
        }
        config.headers[types_1.DefaultRequestHeaderType.APPLICATION_TOKEN] =
            applicationToken;
    }
}
function attachAcceptLanguageHeaderIfPresent(config, locale) {
    if (locale) {
        if (!config.headers) {
            config.headers = {};
        }
        config.headers[types_1.DefaultRequestHeaderType.ACCEPT_LANGUAGE] = locale;
    }
}
function attachCancelTokenIfPresent(config, cancelToken) {
    if (cancelToken) {
        config.cancelToken = cancelToken;
    }
}
function attachPreviewOptionsIfPresent(config, preview) {
    if (preview) {
        if (!config.headers) {
            config.headers = {};
        }
        var sandbox = preview.sandbox, token = preview.token, date = preview.date;
        if (sandbox) {
            config.headers[types_1.DefaultRequestHeaderType.PREVIEW_SANDBOX] = sandbox;
        }
        if (token) {
            config.headers[types_1.DefaultRequestHeaderType.PREVIEW_TOKEN] = token;
        }
        if (date) {
            config.headers[types_1.DefaultRequestHeaderType.PREVIEW_DATE] = date;
        }
    }
}
//# sourceMappingURL=client.js.map